/**
 * @fileoverview Template state management hook
 * @description Управление шаблонами email: CRUD операции, форма редактирования
 *
 * Data fetching powered by TanStack Query (useTemplatesQuery).
 * Local state retained for form/UI/mutation concerns.
 */
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../lib/queryKeys';
import { useTemplatesQuery } from '../hooks/outreach/queries';
import {
    createTemplate as apiCreateTemplate,
    updateTemplate as apiUpdateTemplate,
    deleteTemplate as apiDeleteTemplate
} from '../api/outreach/templates';
import type { Template, TemplateFormData } from '../types/outreach';

const createEmptyTemplateForm = (): TemplateFormData => ({
    name: '',
    description: '',
    channel: 'EMAIL',
    subject: '',
    body: ''
});

/**
 * Template state management hook.
 * TanStack Query for data fetching; local state for form/UI.
 */
export const useTemplateState = () => {
    const navigate = useNavigate();
    const qc = useQueryClient();

    // =========================================================================
    // TQ QUERY
    // =========================================================================
    const templatesQuery = useTemplatesQuery();

    // Derived from TQ
    const templates: Template[] = templatesQuery.data || [];
    const templatesLoading = templatesQuery.isLoading;

    // =========================================================================
    // LOCAL UI / FORM STATE
    // =========================================================================
    const [templatesError, setTemplatesError] = useState<string | null>(null);
    const [templateForm, setTemplateForm] = useState<TemplateFormData>(createEmptyTemplateForm);
    const [templateSaving, setTemplateSaving] = useState(false);
    const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
    const [isTemplateEditing, setIsTemplateEditing] = useState(false);
    const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

    // =========================================================================
    // INVALIDATION HELPER
    // =========================================================================
    const invalidateTemplates = useCallback(
        () => qc.invalidateQueries({ queryKey: queryKeys.outreach.templates.all }),
        [qc]
    );

    // =========================================================================
    // FACADE: fetchTemplates (OutreachContext compat)
    // =========================================================================
    const fetchTemplates = useCallback(async (): Promise<Template[]> => {
        await invalidateTemplates();
        return qc.getQueryData<Template[]>(queryKeys.outreach.templates.list()) || [];
    }, [invalidateTemplates, qc]);

    // =========================================================================
    // HELPERS
    // =========================================================================
    const applyTemplateToForm = useCallback((template: Template) => {
        if (!template) return;
        setSelectedTemplateId(template.id);
        setTemplateForm({
            name: template.name || '',
            description: template.description || '',
            channel: template.channel || 'EMAIL',
            subject: template.subject || '',
            body: template.body || ''
        });
    }, []);

    // =========================================================================
    // HANDLERS
    // =========================================================================
    const startCreateTemplate = useCallback(() => {
        setIsCreatingTemplate(true);
        setIsTemplateEditing(true);
        navigate('/outreach/templates/new');
        setSelectedTemplateId(null);
        setTemplateForm(createEmptyTemplateForm());
        setTemplatesError(null);
    }, [navigate]);

    const handleTemplateEdit = useCallback((template: Template) => {
        if (!template) return;
        applyTemplateToForm(template);
        setIsCreatingTemplate(false);
        setIsTemplateEditing(true);
        navigate(`/outreach/templates/${template.id}`);
        setTemplatesError(null);
    }, [applyTemplateToForm, navigate]);

    const handleTemplateFieldChange = useCallback(<K extends keyof TemplateFormData>(
        field: K,
        value: TemplateFormData[K]
    ) => {
        setTemplateForm(prev => ({ ...prev, [field]: value }));
    }, []);

    const handleSaveTemplate = useCallback(async () => {
        const trimmedName = templateForm.name.trim();
        if (!trimmedName) {
            setTemplatesError('Введите название шаблона');
            return;
        }
        setTemplateSaving(true);
        setTemplatesError(null);
        try {
            const payload = {
                name: trimmedName,
                description: templateForm.description?.trim() || null,
                channel: templateForm.channel || 'EMAIL',
                subject: templateForm.subject?.trim() || null,
                body: templateForm.body?.trim() || null
            };

            if (isCreatingTemplate) {
                const created = await apiCreateTemplate(payload) as Template | undefined;
                await invalidateTemplates();
                const items = qc.getQueryData<Template[]>(queryKeys.outreach.templates.list()) || [];
                if (created) {
                    applyTemplateToForm(created);
                } else if (items?.length) {
                    applyTemplateToForm(items[0]);
                }
            } else if (selectedTemplateId) {
                await apiUpdateTemplate(selectedTemplateId, payload);
                await invalidateTemplates();
                const items = qc.getQueryData<Template[]>(queryKeys.outreach.templates.list()) || [];
                if (items?.length) {
                    const updated = items.find(item => item.id === selectedTemplateId) || items[0];
                    applyTemplateToForm(updated);
                }
            }
            setIsCreatingTemplate(false);
            setIsTemplateEditing(false);
            navigate('/outreach/templates');
        } catch (err) {
            console.error('Failed to save template', err);
            const apiError = err as { data?: { error?: string }; message?: string };
            setTemplatesError(apiError?.data?.error || apiError.message || 'Unknown error');
        } finally {
            setTemplateSaving(false);
        }
    }, [templateForm, isCreatingTemplate, selectedTemplateId, invalidateTemplates, applyTemplateToForm, navigate, qc]);

    const handleDeleteTemplate = useCallback(async (templateId?: string) => {
        const targetId = templateId || selectedTemplateId;
        if (!targetId) return;
        if (!window.confirm('Удалить шаблон?')) return;

        setTemplateSaving(true);
        setTemplatesError(null);
        try {
            await apiDeleteTemplate(targetId);
            await invalidateTemplates();
            const items = qc.getQueryData<Template[]>(queryKeys.outreach.templates.list()) || [];
            if (items.length === 0) {
                setTemplateForm(createEmptyTemplateForm());
            } else {
                setIsCreatingTemplate(false);
                setIsTemplateEditing(false);
            }
            setSelectedTemplateId(null);
            navigate('/outreach/templates');
        } catch (err) {
            console.error('Failed to delete template', err);
            const apiError = err as { data?: { error?: string }; message?: string };
            setTemplatesError(apiError?.data?.error || apiError.message || 'Unknown error');
        } finally {
            setTemplateSaving(false);
        }
    }, [invalidateTemplates, selectedTemplateId, navigate, qc]);

    const handleCancelTemplateEdit = useCallback(() => {
        setTemplatesError(null);
        navigate('/outreach/templates');
        if (isCreatingTemplate) {
            setTemplateForm(createEmptyTemplateForm());
            setIsCreatingTemplate(false);
            return;
        }
        setIsTemplateEditing(false);
        if (selectedTemplateId) {
            const template = templates.find(item => item.id === selectedTemplateId);
            if (template) {
                applyTemplateToForm(template);
            } else {
                setTemplateForm(createEmptyTemplateForm());
            }
        } else {
            setTemplateForm(createEmptyTemplateForm());
        }
    }, [applyTemplateToForm, isCreatingTemplate, selectedTemplateId, templates, navigate]);

    return {
        templates,
        templatesLoading,
        templatesError,
        templateForm,
        templateSaving,
        isCreatingTemplate,
        isTemplateEditing,
        isTemplateModalOpen,
        selectedTemplateId,
        setSelectedTemplateId,
        fetchTemplates,
        startCreateTemplate,
        handleTemplateEdit,
        handleTemplateFieldChange,
        handleSaveTemplate,
        handleDeleteTemplate,
        handleCancelTemplateEdit
    };
};

export default useTemplateState;
